import { call, delay, put, take, takeLatest, select, cancel, cancelled } from 'redux-saga/effects';
import { eventChannel, END } from 'redux-saga';
import * as Sentry from '@sentry/react';

import * as api from 'src/store/api';
import { ONBOARDING } from 'src/store/ActionTypes';
import { setRefreshes } from 'src/store/actions/onboarding';

function* fetchOnboardings(action) {
  const { type } = action;
  if (type === ONBOARDING.CANCEL_FETCH_ONBOARDINGS) yield cancel();
  try {
    const {
      payload: { fields = '*', perPage: nextPerPage, page, ...query },
    } = action;
    const perPage = nextPerPage || 10;
    const nextPage = page || 1;
    const options = {
      headers: {
        'X-Fields': fields,
      },
    };
    const payload = yield call(api.fetchOnboardings, query, options, { per_page: perPage, page: nextPage });
    payload.items.forEach((onb) => onb.files.forEach((file) => file.states.sort((a, b) => b.id - a.id)));
    const datasetIds = payload?.items?.map((onb) => onb?.dataset_id);
    const segmentOptions = {
      headers: {
        'X-Fields': 'items{id,datasetId,statuses,dataset}',
      },
    };
    const segmentQuery = {
      filters: [
        {
          name: 'dataset_id',
          op: 'in',
          val: datasetIds,
        },
      ],
    };
    const segments = yield call(api.fetchSegmentsExperimental, segmentQuery, segmentOptions);
    segments?.forEach((segment) => segment.statuses.sort((a, b) => b.id - a.id));
    for (const segment of segments) {
      const onbIndex = payload.items.findIndex((onb) => onb?.dataset_id === segment.datasetId);
      if (onbIndex !== -1) payload.items[onbIndex].segment = segment;
    }
    yield put({
      type: ONBOARDING.FETCH_ONBOARDINGS_SUCCEEDED,
      payload,
    });
  } catch (e) {
    yield put({
      type: ONBOARDING.FETCH_ONBOARDINGS_FAILED,
      payload: e,
    });
  } finally {
    const isCancelled = yield cancelled();
    if (!isCancelled) {
      yield delay(10000);
      yield put(action);
    }
  }
}

function* fetchClientOnboardingConfig(action) {
  try {
    const { type, scopeClientType, tenant } = action.payload;
    const config = yield call(api.fetchClientOnboardingConfig, type, scopeClientType, tenant);
    yield put({
      type: ONBOARDING.FETCH_CLIENT_ONB_CONFIG_SUCCEEDED,
      payload: config,
    });
  } catch (e) {
    yield put({
      type: ONBOARDING.FETCH_CLIENT_ONB_CONFIG_FAILED,
      payload: e.message,
    });
  }
}

function* fetchRefreshList() {
  try {
    const refreshes = yield call(api.fetchRefreshes);
    yield put(setRefreshes(refreshes));
  } catch (err) {
    yield put({
      type: ONBOARDING.FETCH_REFRESHES_FAILED,
      payload: err.toString(),
    });
  } finally {
    yield delay(500);
    yield put({
      type: ONBOARDING.UNSET,
      payload: 'fetchRefreshesStatus',
    });
  }
}

function* getOnboarding(action) {
  try {
    const { payload: id } = action;
    const options = {
      headers: {
        'X-Fields': 'id,dataset_id',
      },
    };
    const onboarding = yield call(api.getOnboarding, id, options);
    yield put({
      type: ONBOARDING.GET_ONBOARDING_SUCCEEDED,
      payload: onboarding,
    });
  } catch (e) {
    yield put({
      type: ONBOARDING.GET_ONBOARDING_FAILED,
      payload: e,
    });
  }
}

function* getUploadFile(action) {
  try {
    const { payload: id } = action;
    const uploadFileList = yield call(api.getUploadFile, id);
    yield put({
      type: ONBOARDING.GET_CUF_SUCCEEDED,
      payload: uploadFileList,
    });
  } catch (e) {
    yield put({
      type: ONBOARDING.GET_CUF_FAILED,
      payload: e,
    });
  }
}

function* getDataset(action) {
  try {
    const { payload: id } = action;
    const options = {
      headers: {
        'X-Fields': 'id,name,payload{onb_column_counts}',
      },
    };
    const onboardings = yield call(api.fetchDataset, id, options);
    yield put({
      type: ONBOARDING.GET_DATASET_SUCCEEDED,
      payload: onboardings,
    });
  } catch (e) {
    yield put({
      type: ONBOARDING.GET_DATASET_FAILED,
      payload: e,
    });
  }
}

function* uploadFiles(action) {
  try {
    const {
      onboarding: { datasetName, datasetTag, onboardingType },
    } = yield select();
    const {
      app: { profile, tenant },
    } = yield select();
    // Payload to associate with the upload
    const userPayload = {
      profile,
      datasetName,
      datasetTag,
      onboardingType,
    };
    const channelFactory = () =>
      eventChannel((emitter) => {
        const {
          payload: { files, keyPrefix },
        } = action;
        const promises = files.map(async (file, index) => {
          const fileMetadata = {
            name: file.name,
            type: file.type,
            size: file.size,
          };
          // check file name for csv extension (MIME not always available)
          // file.name.slice(-4).toLowerCase() gives the extension of the file. However,
          // checks on this extension doesn't improve security but may offer friction
          // for customers who are onboarding with non-standard extension. We have a validation
          // that reads the file after upload and that check should validate if the CSV file
          // is valid
          // Carl NOTE: it may create friction but on certain system configurations MIME type isn't
          // available to check. Consequently, the best effort check is for a csv extension.
          if (!file.name.slice(-4).toLowerCase().includes('csv')) {
            Sentry.captureException(
              `File uploaded by tenant ${tenant} was not a csv. Meta: ${JSON.stringify(fileMetadata, null, 2)}.`
            );
            throw new Error(`File ${file.name} was not a csv file.`);
          }
          if (file.name.length > 60) {
            throw new Error('File name may not exceed 60 characters total (including extension).');
          }

          const key = `${keyPrefix}/${encodeURIComponent(file.name.replaceAll(' ', '_'))}`;
          const url = await api.getFileUploadUrl('', key);
          const parsedUrl = new URL(url);
          const fileBucket = parsedUrl.host.split('.')[0];
          const fileName = parsedUrl.pathname;
          await api.addOnboardingMetadata({
            file_bucket: fileBucket,
            file_name: decodeURIComponent(fileName),
            payload: JSON.stringify(userPayload, null, 0),
          });
          const status = await api.uploadFilePII(url, file, (progress) => {
            emitter({ progress, index });
          });
          return { status, bucket: fileBucket, key: decodeURIComponent(fileName) };
        });
        Promise.all(promises)
          .then((statuses) => {
            emitter({ statuses });
            emitter(END);
          })
          .catch((error) => {
            emitter({ error: error || 'Upload Failed' });
          });
        return () => {};
      });
    const channel = channelFactory();
    while (true) {
      const { progress = 0, index = 0, statuses, error } = yield take(channel);
      if (error) {
        yield put({
          type: ONBOARDING.UPLOAD_FILES_FAILED,
          payload: error,
        });
        return;
      }
      if (statuses?.length) {
        const { bucket, key } = statuses[0];
        yield put({
          type: ONBOARDING.UPLOAD_FILES_SUCCEEDED,
          payload: {
            bucket,
            key,
          },
        });
        return;
      }
      yield put({
        type: ONBOARDING.UPDATE_PROGRESS,
        payload: {
          index,
          progress,
        },
      });
    }
  } catch (error) {
    yield put({
      type: ONBOARDING.UPLOAD_FILES_FAILED,
      payload: error,
    });
  } finally {
    yield delay(500);
    yield put({
      type: ONBOARDING.UNSET,
      payload: 'uploadFileStatus',
    });
  }
}

function* triggerDetWorkflow(action) {
  try {
    yield call(api.triggerDetWorkflow, action.payload);
    yield put({
      type: ONBOARDING.TRIGGER_DET_WORKFLOW_SUCCEEDED,
    });
  } catch (e) {
    yield put({
      type: ONBOARDING.TRIGGER_DET_WORKFLOW_FAILED,
      payload: e.message,
    });
  } finally {
    delay(500);
    yield put({
      type: ONBOARDING.UNSET,
      payload: 'triggerDetWorkflowStatus',
    });
  }
}

function* approveDetWorkflow(action) {
  try {
    yield call(api.approveDetWorkflow, action.payload);
    yield put({
      type: ONBOARDING.APPROVE_DET_WORKFLOW_SUCCEEDED,
    });
  } catch (e) {
    yield put({
      type: ONBOARDING.APPROVE_DET_WORKFLOW_FAILED,
      payload: e.message,
    });
  } finally {
    delay(500);
    yield put({
      type: ONBOARDING.UNSET,
      payload: 'approveDetWorkflowStatus',
    });
  }
}

function* confirmDetWorkflow(action) {
  try {
    yield call(api.confirmDetWorkflow, action.payload);
    yield put({
      type: ONBOARDING.CONFIRM_DET_WORKFLOW_SUCCEEDED,
    });
  } catch (e) {
    yield put({
      type: ONBOARDING.CONFIRM_DET_WORKFLOW_FAILED,
      payload: e.message,
    });
  } finally {
    delay(500);
    yield put({
      type: ONBOARDING.UNSET,
      payload: 'confirmDetWorkflowStatus',
    });
  }
}

function* fetchClientByTenant(action) {
  try {
    const client = yield call(api.fetchClientByTenant, action.payload);
    yield put({
      type: ONBOARDING.FETCH_CLIENT_BY_TENANT_SUCCEEDED,
      payload: client,
    });
  } catch (e) {
    yield put({
      type: ONBOARDING.FETCH_CLIENT_BY_TENANT_FAILED,
      payload: e.message || e.toString(),
    });
  } finally {
    delay(500);
    yield put({
      type: ONBOARDING.UNSET,
      payload: 'fetchClientByTenantStatus',
    });
  }
}

function* selectGridRow(action) {
  const { payload: row } = action;
  const {
    onboarding: { maxGridRowSelection, selectedGridRows },
  } = yield select();
  if (selectedGridRows.length < maxGridRowSelection) {
    yield put({
      type: ONBOARDING.SET_SELECTED_GRID_ROWS,
      payload: [...selectedGridRows, row],
    });
  } else {
    const [, ...restRows] = selectedGridRows;
    yield put({
      type: ONBOARDING.SET_SELECTED_GRID_ROWS,
      payload: [...restRows, row],
    });
  }
}

function* deselectGridRow(action) {
  const { payload: row } = action;
  const {
    onboarding: { selectedGridRows },
  } = yield select();
  const filteredSelectedGridRows = selectedGridRows.filter(({ id }) => id !== row.id);
  yield put({
    type: ONBOARDING.SET_SELECTED_GRID_ROWS,
    payload: filteredSelectedGridRows,
  });
}

function* uploadFilesSaga() {
  yield takeLatest(ONBOARDING.UPLOAD_FILES, uploadFiles);
}

function* fetchOnboardingsSaga() {
  yield takeLatest([ONBOARDING.FETCH_ONBOARDINGS, ONBOARDING.CANCEL_FETCH_ONBOARDINGS], fetchOnboardings);
}

function* fetchClientOnboardingConfigSaga() {
  yield takeLatest(ONBOARDING.FETCH_CLIENT_ONB_CONFIG, fetchClientOnboardingConfig);
}

function* fetchRefreshListSaga() {
  yield takeLatest(ONBOARDING.FETCH_REFRESHES, fetchRefreshList);
}

function* getOnboardingSaga() {
  yield takeLatest(ONBOARDING.GET_ONBOARDING, getOnboarding);
}

function* getUploadFileSaga() {
  yield takeLatest(ONBOARDING.GET_CUF, getUploadFile);
}

function* getDatasetSaga() {
  yield takeLatest(ONBOARDING.GET_DATASET, getDataset);
}

function* triggerDetWorkflowSaga() {
  yield takeLatest(ONBOARDING.TRIGGER_DET_WORKFLOW, triggerDetWorkflow);
}

function* approveDetWorkflowSaga() {
  yield takeLatest(ONBOARDING.APPROVE_DET_WORKFLOW, approveDetWorkflow);
}

function* confirmDetWorkflowSaga() {
  yield takeLatest(ONBOARDING.CONFIRM_DET_WORKFLOW, confirmDetWorkflow);
}

function* fetchClientByTenantSaga() {
  yield takeLatest(ONBOARDING.FETCH_CLIENT_BY_TENANT, fetchClientByTenant);
}

function* onRowSelectSaga() {
  yield takeLatest(ONBOARDING.SELECT_GRID_ROW, selectGridRow);
}

function* onRowDeselectSaga() {
  yield takeLatest(ONBOARDING.DESELECT_GRID_ROW, deselectGridRow);
}

export default [
  uploadFilesSaga(),
  fetchOnboardingsSaga(),
  fetchClientOnboardingConfigSaga(),
  fetchRefreshListSaga(),
  getUploadFileSaga(),
  getOnboardingSaga(),
  getDatasetSaga(),
  triggerDetWorkflowSaga(),
  approveDetWorkflowSaga(),
  confirmDetWorkflowSaga(),
  fetchClientByTenantSaga(),
  onRowSelectSaga(),
  onRowDeselectSaga(),
];
